import { AlertCircle, Eye, EyeOff, KeyRound, Shield } from "lucide-react";
import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate } from "react-router-dom";
import SecondFactorStep from "../../components/auth/SecondFactorStep";
import LanguageSwitcher from "../../components/ui/LanguageSwitcher";
import {
  SecondFactorRequiredError,
  type FactorChallenge,
} from "../../services/secondFactor";
import {
  isUserCancellation,
  passkeyService,
} from "../../services/passkey.service";
import { createCheckoutSession } from "../../services/stripe";
import { useAuthStore } from "../../stores/authStore";
import { useI18nStore } from "../../stores/i18nStore";
import { rememberPlanIntent, takePlanIntent } from "../../utils/planIntent";
import { isStaffAdmin } from "@/utils/staffRoles";
import { log } from "@/lib/log";
import { issuerReturn } from "@/utils/issuerReturn";

/**
 * Where a fresh sign-in lands.
 *
 * Staff go to the back office, everyone else to the traveller dashboard. Read
 * from the store rather than from a closure: `login()` has just written the
 * token, and the role lives in that token, not in the profile.
 */
const landingAfterLogin = (): string =>
  isStaffAdmin(useAuthStore.getState().token)
    ? "/admin/dashboard"
    : "/dashboard";

export const LoginPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  /** Set once the password has been accepted and a factor is owed. */
  const [challenge, setChallenge] = useState<FactorChallenge | null>(null);
  const [passkeyLoading, setPasskeyLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = new URLSearchParams(location.search).get("returnTo");
  // ComDesk's sign-in is a backend URL, left by a full page load (IssuerReturn).
  const leaveLogin = () => {
    const issuer = issuerReturn(returnTo);
    if (issuer) window.location.assign(issuer);
    else navigate(returnTo ?? landingAfterLogin());
  };
  const { login } = useAuthStore();

  // A plan chosen before signing in (?plan=plus) opens checkout once signed
  // in, the same as after registration.
  React.useEffect(() => {
    rememberPlanIntent(location.search);
  }, [location.search]);
  const continueToCheckout = async (): Promise<boolean> => {
    const plan = takePlanIntent();
    const token = useAuthStore.getState().token;
    if (!plan || !token) return false;
    try {
      return await createCheckoutSession(plan, token);
    } catch {
      return false;
    }
  };
  const { t, resetToEnglish } = useI18nStore();

  // English is the default on the sign-in page; reset on mount.
  React.useEffect(() => {
    log.debug("LoginPage mounted, resetting to English");
    resetToEnglish();
  }, [resetToEnglish]);

  /**
   * One sign-in attempt. Called twice: once with the password, and again with
   * the factor once the first attempt says one is owed.
   */
  const attempt = async (factor?: string, mfaToken?: string) => {
    setIsLoading(true);
    setError("");

    try {
      await login({
        email,
        password,
        ...(factor ? { factor } : {}),
        ...(mfaToken ? { mfaToken } : {}),
      });

      if (await continueToCheckout()) return;
      // Small delay to ensure auth state is properly set
      setTimeout(() => {
        leaveLogin();
      }, 100);
    } catch (err: any) {
      // The password was right; the account owes a second factor. Switching to
      // the factor step rather than showing an error, because nothing failed.
      if (err instanceof SecondFactorRequiredError) {
        if (!err.challenge) {
          // No challenge means the backend could not start a factor. Saying so
          // is better than showing an empty prompt the user cannot satisfy.
          setError(t.auth2SecondStepFailed);
        } else {
          setChallenge(err.challenge);
        }
        setIsLoading(false);
        return;
      }

      // Keycloak's brute-force detection answers a paused account exactly as
      // it answers a wrong password, so the message has to cover both.
      setError(
        /invalid (user )?credentials/i.test(err?.message ?? "")
          ? t.loginInvalidOrPaused
          : err?.message || t.loginFailed,
      );
      setIsLoading(false);
    }
  };

  /**
   * Signing in with a passkey and nothing else.
   *
   * No email is typed first: the credential is discoverable, so the
   * authenticator offers what it holds for this site and the one that signs
   * names the account. What comes back goes to the same login endpoint the
   * password uses, with the proof in place of the password — so the token still
   * comes from Keycloak, by the same route, with the same session handling.
   */
  const signInWithPasskey = async () => {
    setPasskeyLoading(true);
    setError("");

    try {
      const { email: passkeyEmail, passkeyProof } =
        await passkeyService.signIn();
      await login({ email: passkeyEmail, passkeyProof });
      if (await continueToCheckout()) return;
      // The same short delay the password path uses: the store is persisted
      // asynchronously and the dashboard reads it on mount.
      setTimeout(() => {
        leaveLogin();
      }, 100);
    } catch (err) {
      // Dismissing the system prompt is a decision, not a failure; saying
      // "that passkey could not be used" would be telling the user something
      // went wrong when they simply changed their mind.
      if (!isUserCancellation(err)) {
        setError(err instanceof Error ? err.message : t.auth2PasskeyFailed);
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      setError(t.required);
      return;
    }

    await attempt();
  };

  /**
   * Back to the start. The password is dropped rather than kept, so a shared
   * or unattended screen does not leave it sitting in a form field.
   */
  const restart = () => {
    setChallenge(null);
    setPassword("");
    setError("");
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--ui-auth-backdrop)",
        fontFamily:
          'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* A sign-in form answers no search; keep it out of the index. */}
      <Helmet>
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      {/* Background Effects */}
      <div
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          width: "300px",
          height: "300px",
          background:
            "radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(40px)",
          zIndex: 1,
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "300px",
          height: "300px",
          background:
            "radial-gradient(circle, rgba(34, 197, 94, 0.1) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(40px)",
          zIndex: 1,
        }}
      />

      {/* Header */}
      <header
        style={{
          position: "relative",
          zIndex: 10,
          padding: "24px",
        }}
      ></header>

      {/* Main Content */}
      <main
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px",
          position: "relative",
          zIndex: 10,
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: "420px",
            margin: "0 auto",
          }}
        >
          {/* HealthPass Logo */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: "32px",
              width: "100%",
              maxWidth: "420px",
            }}
          >
            <img
              src="/images/healthpass_logo.png"
              alt={t.auth2LogoAlt}
              style={{
                width: "100%",
                maxWidth: "320px",
                height: "auto",
              }}
            />
          </div>

          {/* Password first, then the factor. The two are separate screens
              rather than one form, so the code field is never on screen
              before the backend has said which factor this account uses. */}
          {challenge ? (
            <SecondFactorStep
              challenge={challenge}
              emailAddress={email}
              onSubmit={attempt}
              onCancel={restart}
              error={error}
              isSubmitting={isLoading}
            />
          ) : (
            <>
              {/* Welcome Section */}
              <div
                style={{
                  textAlign: "center",
                  marginBottom: "32px",
                }}
              >
                <h2
                  style={{
                    fontSize: "28px",
                    fontWeight: "bold",
                    color: "var(--ui-text-strong)",
                    margin: "0 0 8px 0",
                    lineHeight: "1.3",
                  }}
                >
                  {t.welcomeBack}
                </h2>
                <p
                  style={{
                    color: "var(--ui-text-muted)",
                    fontSize: "16px",
                    margin: 0,
                    lineHeight: "1.5",
                  }}
                >
                  {t.signInToAccount}
                </p>
              </div>

              {/* Login Form */}
              <div
                style={{
                  background: "var(--ui-glass)",
                  backdropFilter: "blur(20px)",
                  borderRadius: "24px",
                  padding: "32px",
                  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  position: "relative",
                  marginBottom: "24px",
                }}
              >
                <form
                  onSubmit={handleSubmit}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px",
                  }}
                >
                  {/* Language Selector */}
                  <div style={{ marginBottom: "8px" }}>
                    <LanguageSwitcher />
                  </div>

                  {/* Email Field */}
                  <div>
                    <label
                      htmlFor="email"
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "var(--ui-text-body)",
                        marginBottom: "6px",
                      }}
                    >
                      {t.email}*
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      placeholder={t.auth2EmailPlaceholder}
                      value={email}
                      required
                      autoComplete="email"
                      data-testid="email-input"
                      style={{
                        width: "100%",
                        padding: "14px 16px",
                        background: "var(--ui-field)",
                        border: "1px solid var(--ui-line)",
                        borderRadius: "12px",
                        fontSize: "16px",
                        outline: "none",
                        transition: "all 0.2s",
                        boxSizing: "border-box",
                      }}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={isLoading}
                      onFocus={(e) =>
                        (e.target.style.borderColor = "var(--ui-link)")
                      }
                      onBlur={(e) =>
                        (e.target.style.borderColor = "var(--ui-line)")
                      }
                    />
                  </div>

                  {/* Password Field */}
                  <div>
                    <label
                      htmlFor="password"
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "var(--ui-text-body)",
                        marginBottom: "6px",
                      }}
                    >
                      {t.password}*
                    </label>
                    <div style={{ position: "relative" }}>
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder={t.password}
                        value={password}
                        required
                        autoComplete="current-password"
                        data-testid="password-input"
                        style={{
                          width: "100%",
                          padding: "14px 16px",
                          paddingRight: "48px",
                          background: "var(--ui-field)",
                          border: "1px solid var(--ui-line)",
                          borderRadius: "12px",
                          fontSize: "16px",
                          outline: "none",
                          transition: "all 0.2s",
                          boxSizing: "border-box",
                        }}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={isLoading}
                        onFocus={(e) =>
                          (e.target.style.borderColor = "var(--ui-link)")
                        }
                        onBlur={(e) =>
                          (e.target.style.borderColor = "var(--ui-line)")
                        }
                      />
                      <button
                        type="button"
                        style={{
                          position: "absolute",
                          right: "14px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "none",
                          border: "none",
                          color: "var(--ui-text-muted)",
                          cursor: "pointer",
                          padding: "4px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={
                          showPassword
                            ? t.auth2HidePassword
                            : t.auth2ShowPassword
                        }
                        disabled={isLoading}
                      >
                        {showPassword ? (
                          <EyeOff style={{ width: "20px", height: "20px" }} />
                        ) : (
                          <Eye style={{ width: "20px", height: "20px" }} />
                        )}
                      </button>
                    </div>
                    <div
                      style={{
                        marginTop: "8px",
                        textAlign: "right",
                      }}
                    >
                      <a
                        href="/forgot-password"
                        style={{
                          fontSize: "14px",
                          color: "var(--ui-link)",
                          textDecoration: "none",
                        }}
                      >
                        {t.forgotPassword}
                      </a>
                    </div>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        color: "var(--ui-error-fg)",
                        background: "var(--ui-error-bg)",
                        padding: "12px 16px",
                        borderRadius: "12px",
                        border: "1px solid var(--ui-error-line)",
                      }}
                    >
                      <AlertCircle
                        style={{ width: "20px", height: "20px", flexShrink: 0 }}
                      />
                      <span style={{ fontSize: "14px", fontWeight: "500" }}>
                        {error}
                      </span>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isLoading}
                    data-testid="login-submit-button"
                    style={{
                      width: "100%",
                      height: "50px",
                      background: "linear-gradient(45deg, #3b82f6, #22c55e)",
                      color: "white",
                      border: "none",
                      borderRadius: "12px",
                      fontSize: "16px",
                      fontWeight: "600",
                      cursor: isLoading ? "not-allowed" : "pointer",
                      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                      transition: "all 0.2s",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      opacity: isLoading ? 0.8 : 1,
                    }}
                  >
                    {isLoading ? (
                      <>
                        <div
                          style={{
                            width: "20px",
                            height: "20px",
                            border: "2px solid rgba(255,255,255,0.3)",
                            borderTop: "2px solid white",
                            borderRadius: "50%",
                            animation: "spin 1s linear infinite",
                          }}
                        />
                        <span>{t.auth2SigningIn}</span>
                      </>
                    ) : (
                      <>
                        <Shield style={{ width: "20px", height: "20px" }} />
                        <span>{t.signIn}</span>
                      </>
                    )}
                  </button>
                </form>

                {/* Signing in with a passkey instead of a password.
                
                An earlier version of this block called Keycloak endpoints that
                do not exist, so it could only ever fail, and it was removed;
                what replaces it runs against our own backend, which is where
                the credentials are, and still gets its tokens from Keycloak.
                
                No email field above it: the credential is discoverable, so the
                authenticator offers what it holds for this site and whichever
                one signs names the account. Shown to every visitor rather than
                only to those with a passkey — the page has no way to know who
                is looking at it, and the authenticator says "no passkey for
                this site" better than we could. */}
                {passkeyService.isSupported() && (
                  <>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        margin: "20px 0",
                      }}
                    >
                      <div
                        style={{
                          flex: 1,
                          height: "1px",
                          background: "var(--ui-line-soft)",
                        }}
                      />
                      <span
                        style={{
                          fontSize: "13px",
                          color: "var(--ui-text-muted)",
                        }}
                      >
                        {t.auth2Or}
                      </span>
                      <div
                        style={{
                          flex: 1,
                          height: "1px",
                          background: "var(--ui-line-soft)",
                        }}
                      />
                    </div>

                    <button
                      type="button"
                      onClick={signInWithPasskey}
                      disabled={passkeyLoading || isLoading}
                      data-testid="login-passkey-button"
                      style={{
                        width: "100%",
                        height: "50px",
                        background: "var(--ui-field)",
                        color: "var(--ui-text-strong)",
                        border: "1px solid var(--ui-line)",
                        borderRadius: "12px",
                        fontSize: "16px",
                        fontWeight: "600",
                        cursor:
                          passkeyLoading || isLoading
                            ? "not-allowed"
                            : "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                        opacity: passkeyLoading || isLoading ? 0.6 : 1,
                        transition: "all 0.2s",
                      }}
                    >
                      <KeyRound style={{ width: "20px", height: "20px" }} />
                      <span>
                        {passkeyLoading
                          ? t.auth2WaitingForPasskey
                          : t.auth2SignInWithPasskey}
                      </span>
                    </button>
                  </>
                )}

                {/* Register Link */}
                <div
                  style={{
                    marginTop: "24px",
                    textAlign: "center",
                    paddingTop: "20px",
                    borderTop: "1px solid var(--ui-line-soft)",
                  }}
                >
                  <p
                    style={{
                      fontSize: "14px",
                      color: "var(--ui-text-muted)",
                      margin: "0 0 12px 0",
                    }}
                  >
                    {t.noAccountYet}{" "}
                    <a
                      href={`/traveler/register${location.search}`}
                      style={{
                        color: "var(--ui-link)",
                        textDecoration: "none",
                        fontWeight: "500",
                      }}
                    >
                      {t.createFreeAccount}
                    </a>
                  </p>
                </div>
              </div>
            </>
          )}

          {/* Footer */}
          <div
            style={{
              textAlign: "center",
              fontSize: "14px",
              color: "var(--ui-text-muted)",
            }}
          >
            <p style={{ margin: "0 0 8px 0" }}>
              © {new Date().getFullYear()} HealthPass
            </p>
            <div
              style={{
                marginTop: "12px",
                display: "flex",
                justifyContent: "center",
                gap: "16px",
                fontSize: "12px",
              }}
            >
              <a
                href="/privacy"
                style={{ color: "var(--ui-link)", textDecoration: "none" }}
              >
                {t.privacyPolicy}
              </a>
              <a
                href="/terms"
                style={{ color: "var(--ui-link)", textDecoration: "none" }}
              >
                {t.termsAndConditions}
              </a>
              <a
                href="/imprint"
                style={{ color: "var(--ui-link)", textDecoration: "none" }}
              >
                {t.auth2Imprint}
              </a>
            </div>
          </div>
        </div>
      </main>

      {/* CSS animation for the spinner */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};
